import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ApiService from "../../../../../../services/ApiService";
import { useAuth } from "../../../../../Providers/LandmarkAuthContext";

const BASE = "landmark/bd";

// Module level response cache so navigating Org -> Team -> Member and back is
// instant. Cleared when the sync version bumps (after a manual refresh).
const cache = new Map();

export const BdApiContext = createContext({ version: 0, bump: () => {} });

export function useBdApiContext() {
  return useContext(BdApiContext);
}

export function useBdApiClient() {
  const { employee } = useAuth();
  return useMemo(() => new ApiService({ employee }), [employee]);
}

function cacheKey(path, params, version) {
  return `${version}|${path}|${JSON.stringify(params || {})}`;
}

/**
 * Fetches `landmark/bd/<path>` with query params. Re-fetches whenever the
 * path/params change or the global data version bumps.
 */
export function useBdQuery(path, params, { enabled = true } = {}) {
  const api = useBdApiClient();
  const { version } = useBdApiContext();
  const key = cacheKey(path, params, version);
  const [state, setState] = useState(() => {
    const cached = cache.get(key);
    return { data: cached || null, loading: !cached && enabled, error: null, key };
  });
  const latestKey = useRef(key);

  const run = useCallback(
    async (force = false) => {
      if (!enabled) return;
      latestKey.current = key;
      if (!force && cache.has(key)) {
        setState({ data: cache.get(key), loading: false, error: null, key });
        return;
      }
      setState((prev) => ({ ...prev, loading: true, error: null, key }));
      try {
        const res = await api.get(`${BASE}/${path}`, params);
        if (latestKey.current !== key) return; // stale
        if (!res || res.success === false || res.error) {
          setState({ data: null, loading: false, error: (res && (res.error || res.message)) || "Request failed", key });
          return;
        }
        cache.set(key, res);
        setState({ data: res, loading: false, error: null, key });
      } catch (err) {
        if (latestKey.current !== key) return;
        setState({ data: null, loading: false, error: err.message || "Request failed", key });
      }
    },
    [api, key, path, params, enabled]
  );

  useEffect(() => {
    run(false);
  }, [run]);

  return { ...state, refetch: () => run(true), isStale: state.key !== key };
}

export function clearBdCache() {
  cache.clear();
}

// Data-source registry (which Salesforce report each number comes from),
// served by GET /meta as `sources` and provided by the dashboard shell so
// <SourceTip> can resolve a metric key anywhere in the tree.
export const BdSourcesContext = createContext({ sources: null, sync: null });

export function useSources() {
  return useContext(BdSourcesContext);
}

// Absolute URL for a user's stored profile picture (served by the API).
// `avatarUrl` from the API is relative to the API base, e.g.
// "landmark/bd/avatars/<sfUserId>?v=123".
const API_BASE_URL = new ApiService().apiUrl;
export function avatarSrc(user) {
  if (!user || !user.avatarUrl) return "";
  return `${API_BASE_URL}${String(user.avatarUrl).replace(/^\/+/, "")}`;
}
